import { useCallback, useEffect, useRef } from 'react';
import { AuthContainer, AuthUser } from '@hssoft/auth-web-shared-lib';
import { Navigate, useNavigate } from 'react-router-dom';

import appVersion from '../../app-version.json';
import { isBrowserOutdated } from '../helpers/browsersSupport/browserSupport.helper';
import { storageHelper } from '../helpers/storage';
import {
  hasRecordedLoginThisSession,
  markLoginRecordedThisSession,
} from '../services/userActivity/loginRecordSession';
import { recordUserEvent } from '../services/userActivity/userActivity.api';
import { WAWIASSIST_APP_ID } from '../services/userActivity/userActivity.types';
import { AUTH_LANGUAGES, SIGN_IN_TITLE_BY_LOCALE, useAuthLanguage } from './authLanguage';

// Sign-in screen mounted at /login. AuthContainer renders the auth forms while the
// user is unauthenticated and, once authenticated, renders its child — which sends
// the user into the app.
export const LoginGate = () => {
  const { selectedLanguage, onLanguageChange } = useAuthLanguage();
  const navigate = useNavigate();
  // Guards a single LOGIN recording per component mount; the session guard below also blocks
  // re-sends across page refreshes within the same browser session.
  const hasRecordedLoginRef = useRef(false);

  const handleAuthenticated = useCallback((user: AuthUser) => {
    // Record the successful login at most once per mount and once per browser session (a page
    // refresh must not re-send it). Fire-and-forget: telemetry must never block or break login.
    if (!hasRecordedLoginRef.current && !hasRecordedLoginThisSession(user.userId)) {
      hasRecordedLoginRef.current = true;
      markLoginRecordedThisSession(user.userId);
      void recordUserEvent({
        type: 'LOGIN',
        appId: WAWIASSIST_APP_ID,
        appVersion: appVersion.version,
      });
    }
  }, []);

  useEffect(() => {
    // Block the sign-in screen on unsupported browsers — the legacy login page
    // gated this and we keep the same contract for direct visits to /login.
    if (isBrowserOutdated()) {
      navigate('/outdated-browsers', { replace: true });
      return;
    }
    // Clear auth-specific session keys before showing the form so a fresh sign-in
    // does not inherit FAW access from a previous session in the same browser tab.
    // sessionStorage.userAccessParams is read by lazy.util's loader cache and would
    // otherwise grant the new user the previous user's modules.
    storageHelper.session.removeItem('userAccessParams');
    storageHelper.session.removeItem('alternativeGuid');
  }, []);

  return (
    <AuthContainer
      hideSignUp
      appVersion={appVersion.version}
      onAuthenticated={handleAuthenticated}
      signInFormProps={{
        logoSrc: '/wawi-logo.svg',
        labels: {
          title: SIGN_IN_TITLE_BY_LOCALE[selectedLanguage] ?? SIGN_IN_TITLE_BY_LOCALE.de,
        },
      }}
      languageProps={{
        languages: AUTH_LANGUAGES,
        selectedLanguage,
        onLanguageChange,
      }}
      mfaChallengeFormProps={{ enableDeviceTrust: true }}
    >
      <Navigate to='/customer/general' replace />
    </AuthContainer>
  );
};
